import { ValueGetterParams, IDateFilterParams, FirstDataRenderedEvent, ColumnState } from 'ag-grid-community';

import { dayjs } from '@parspec/pixel';

import { DISCOUNT_FIELDS, TAB_LABEL } from '../constants';
import { BomTablesDataType, SyncTypeForTable } from './types';
import { BomInfoForDashboard, DocInfo, OMInfoForDashboard, OutofSyncResponse, QuoteDocInfo, QuoteInfoDashBoard, SortPayload, SubmittalInfoForDashboard } from '../../queries/apiTypes';
import { getDate } from '../../../Project/shared/utils';
import { isEmpty, isNil, isObjectLike } from 'lodash-es';
import { BOM_SCOPE_OPTIONS } from 'src/features/shared/constants';

type TabLableKey = keyof typeof TAB_LABEL;
type TabLabelVal = (typeof TAB_LABEL)[TabLableKey];

export function getDateForTable(date: string | null) {
    if (!date) {
        return null;
    }
    return dayjs(date).format('MMM D, YYYY hh:mm A');
}

export function getBomRowData(tabType: TabLabelVal) {
    return (apiData: (BomInfoForDashboard | SubmittalInfoForDashboard | OMInfoForDashboard | QuoteInfoDashBoard) & SyncTypeForTable): BomTablesDataType => {
        const projectId = apiData.project.id;
        const projectName = apiData.project.name;
        const bomId = tabType === TAB_LABEL.BOM_TAB_LABEL ? (apiData as BomInfoForDashboard)?.bom?.id : (apiData as SubmittalInfoForDashboard | OMInfoForDashboard | QuoteInfoDashBoard)?.bom?.id;
        const bomName = tabType === TAB_LABEL.BOM_TAB_LABEL ? (apiData as BomInfoForDashboard)?.bom?.name : (apiData as SubmittalInfoForDashboard | OMInfoForDashboard | QuoteInfoDashBoard)?.bom?.name;
        const primaryContact = tabType === TAB_LABEL.BOM_TAB_LABEL ? (apiData as BomInfoForDashboard)?.bom?.primary_contact : (apiData as QuoteInfoDashBoard)?.bom?.primary_contact;
        const dueDate =
            tabType === TAB_LABEL.SUBMITTAL_TAB_LABEL
                ? apiData.project.submittal_due_date
                : tabType === TAB_LABEL.OM_TAB_LABEL
                ? apiData.project.o_m_due_date
                : tabType === TAB_LABEL.QUOTE_LABEL
                ? apiData.project.quote_due_date
                : null;
        const dueDateForUi = getDateForTable(dueDate);
        let submittalData: DocInfo[] | null | undefined;
        let omData: DocInfo[] | null | undefined;
        let quoteData: QuoteDocInfo[] | null | undefined;
        if (tabType === TAB_LABEL.SUBMITTAL_TAB_LABEL) {
            submittalData = (apiData as SubmittalInfoForDashboard).submittal;
        } else {
            submittalData = (apiData as BomInfoForDashboard).submittal ? (apiData as BomInfoForDashboard).submittal : null;
        }

        if (tabType === TAB_LABEL.OM_TAB_LABEL) {
            omData = (apiData as OMInfoForDashboard).o_m;
        } else {
            omData = (apiData as BomInfoForDashboard).o_m ? (apiData as BomInfoForDashboard).o_m : null;
        }

        if (tabType === TAB_LABEL.QUOTE_LABEL) {
            quoteData = (apiData as QuoteInfoDashBoard).quote;
        } else {
            quoteData = (apiData as QuoteInfoDashBoard).quote ? (apiData as BomInfoForDashboard).quote : null;
        }

        const secondary_contact = tabType === TAB_LABEL.BOM_TAB_LABEL ? (apiData as BomInfoForDashboard)?.bom?.secondary_contact : '';
        const salesAgent = tabType === TAB_LABEL.BOM_TAB_LABEL ? (apiData as BomInfoForDashboard)?.bom?.sales_agent : null;
        const lastEditedAt = tabType === TAB_LABEL.BOM_TAB_LABEL ? (apiData as BomInfoForDashboard)?.bom?.last_edited_at : null;
        const lastEditedBy = tabType === TAB_LABEL.BOM_TAB_LABEL ? (apiData as BomInfoForDashboard)?.bom?.last_edited_by : null;

        return {
            omData,
            bomName,
            projectStage: apiData.project.stage,
            branchLocation: apiData.company.name,
            submittalOwner: apiData.project.submittal_owner,
            omOwner: apiData.project.o_m_owner,
            omOutOfSync: apiData.omOutOfSync,
            submittalOutOfSync: apiData.submittalOutOfSync,
            quoteOutOfSync: apiData.quoteOutOfSync,
            submittalData,
            dueDate,
            dueDateForUi,
            projectId,
            projectName,
            bomId,
            quoteData,
            quoteOwner: apiData.project.quote_owner,
            primary_contact: primaryContact,
            quoteStatus: (apiData as BomInfoForDashboard).quoteStatus,
            submittalStatus: (apiData as BomInfoForDashboard).submittalStatus,
            omStatus: (apiData as BomInfoForDashboard).omStatus,
            secondary_contact,
            salesAgent,
            lastEditedAt,
            lastEditedBy,
            submittalDueDate: apiData.project.submittal_due_date,
            omDueDate: apiData.project.o_m_due_date,
            quoteDueDate: apiData.project.quote_due_date,
            estimatedProjectCost: apiData.project.estimated_project_cost,
            grandTotalCents: apiData.bom.grand_total_cents,
            isPrimary: apiData.bom.is_primary,
            isLinked: apiData.bom.is_linked,
            bomCreationDate: apiData.bom.created_at,
            erpId: apiData.project.erp_id,
            scope: apiData.bom.scope
        };
    };
}

export function getSyncCache(data: OutofSyncResponse) {
    const result = data.reduce((acc, val) => {
        acc[val.id] = val.out_of_sync;
        return acc;
    }, {} as Record<number, boolean>);

    return result;
}

export const getDocumentOwnerTemplate = (props: any, tabName: 'SUBMITTAL' | 'OM' | 'QUOTE') => {
    const { data } = props;

    if (tabName === 'SUBMITTAL') {
        return data?.submittalOwner || '-';
    } else if (tabName === 'OM') {
        return data?.omOwner || '-';
    } else if (tabName === 'QUOTE') {
        return data?.quoteOwner || '-';
    } else {
        return '-';
    }
};

export const dateComparator = (date1: string, date2: string) => {
    const date1Number = date1 ? new Date(date1).getTime() : 0;
    const date2Number = date2 ? new Date(date2).getTime() : 0;

    if (date1Number == null && date2Number == null) {
        return 0;
    }

    if (date1Number == null) {
        return -1;
    } else if (date2Number == null) {
        return 1;
    }

    return date1Number - date2Number;
};

export const stringComparator = (valueA: string, valueB: string) => {
    const a = valueA?.toLowerCase().trim();
    const b = valueB?.toLowerCase().trim();

    if (!a && !b) return 0;

    // If a is empty|null|undefined, return -1 to keep a(empty value) first in ascending order)
    if (!a) return -1;
    // If b is empty|null|undefined, return 1 to keep b(empty value) first in ascending order)
    if (!b) return 1;
    return a.localeCompare(b);
};

export const numberComparator = (valueA: number, valueB: number) => {
    if (valueA > valueB) {
        return 1;
    }
    if (valueA < valueB) {
        return -1;
    }
    return 0;
};

export const getDateComparator = (
    _valueA: string,
    _valueB: string,
    nodeA: any,
    nodeB: any,
    _isDescending: boolean,
    fieldName: 'quoteDueDate' | 'submittalDueDate' | 'omDueDate' | 'quoteCreationDate' | 'submittalCreationDate' | 'omCreationDate' | 'lastEditedAt' | 'dueDate' | 'last_edited_at' | 'bomCreationDate'
) => {
    const date1 = nodeA?.data?.[fieldName] || '';
    const date2 = nodeB?.data?.[fieldName] || '';

    return dateComparator(date1, date2);
};

export const getCustomerContactValue = (params: ValueGetterParams) => {
    const { data, column } = params;
    const contactType = column.getColId();
    return data[contactType]?.company?.name;
};

export const getDateValueForUI = (
    params: ValueGetterParams,
    dateField: 'quoteDueDate' | 'submittalDueDate' | 'omDueDate' | 'dueDate' | 'quoteCreationDate' | 'submittalCreationDate' | 'omCreationDate' | 'bomCreationDate'
) => {
    // Takes in date values and converts them to redable UI format:
    // For ex: 2024-04-29T16:11:00Z ---> Apr 29, 2024 04:11 PM
    const { data } = params;
    const dateForUi = getDateForTable(data[dateField]);

    return dateForUi;
};

export const getStakeholderValue = (params: any, stakeholderType: '0' | '1' | '2' | '3' | '4') => {
    const { data } = params;
    return data?.stakeholders[stakeholderType]?.company?.name;
};

export const getLastModifiedValue = (params: any) => {
    const { data } = params;

    const updatedAt = data?.lastEditedAt;
    const updatedBy = data?.lastEditedBy;

    const dateTime = getDate(updatedAt);

    return `${updatedBy} - ${dateTime}`;
};

export const getCustomerFilterText = (args: any, customerType: 'primary_contact' | 'secondary_contact') => {
    const { company, contact } = args.data[customerType];
    const { name: companyName, business_type: businessType } = company || {};
    const { name: contactName, role } = contact || {};

    return `${companyName} ${contactName} ${businessType} ${role}`;
};

export const dateFilterParams: IDateFilterParams = {
    maxNumConditions: 1,
    buttons: ['clear', 'apply'],
    comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
        if (cellValue == null) return -1;

        // Parse the cell value into a Date object
        const cellDate = new Date(cellValue);

        // Extract the date parts without time
        const cellDateWithoutTime = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());

        // Extract the filter date parts without time
        const filterDateWithoutTime = new Date(filterLocalDateAtMidnight.getFullYear(), filterLocalDateAtMidnight.getMonth(), filterLocalDateAtMidnight.getDate());

        // Compare the date parts without time
        if (cellDateWithoutTime.getTime() === filterDateWithoutTime.getTime()) {
            return 0;
        }
        if (cellDateWithoutTime < filterDateWithoutTime) {
            return -1;
        }
        if (cellDateWithoutTime > filterDateWithoutTime) {
            return 1;
        }
        return 0;
    }
};

export const getTimeStampForAPI = () => {
    const d = new Date();
    const timeStamp =
        d.getFullYear().toString() +
        '-' +
        ('0' + (d.getMonth() + 1)).slice(-2) +
        '-' +
        ('0' + d.getDate()).slice(-2) +
        ' ' +
        ('0' + d.getHours()).slice(-2) +
        ':' +
        ('0' + d.getMinutes()).slice(-2) +
        ':' +
        ('0' + d.getSeconds()).slice(-2);

    return timeStamp;
};

export const checkIfInDiscountFields = (param: string) => DISCOUNT_FIELDS.some((field) => field === param);
export const applyInitialSortStateInTable = (event: FirstDataRenderedEvent, sortField: SortPayload | null) => {
    if (isEmpty(sortField) || !isObjectLike(sortField)) return;

    const state = Object.entries(sortField).reduce<ColumnState[]>((acc, keyValPair) => {
        const [colId, sortProps] = keyValPair;
        const { sort, sortIndex } = sortProps || {};

        if (colId && sort && !isNil(sortIndex)) {
            acc.push({ colId, sort, sortIndex });
        }
        return acc;
    }, []);

    // set sort of saved columns, and reset other columns sort(if any)
    event.api.applyColumnState({
        state,
        defaultState: {
            // important to say 'null' as undefined means 'do nothing'
            sort: null
        }
    });
};

export const getSortedColumns = (columnsState: ColumnState[]) => {
    return columnsState.reduce<SortPayload>((acc, column) => {
        const { colId, sort, sortIndex } = column;
        if (colId && sort && !isNil(sortIndex)) {
            acc[colId] = { sort, sortIndex };
        }
        return acc;
    }, {});
};

export const getScopeOption = (scope: { label: string; key: string } | string | undefined) => {
    return scope ? BOM_SCOPE_OPTIONS.find((item) => (typeof scope !== 'string' && item.label === scope?.label) || scope === item.label) : { key: '', label: '' };
};
